import React from "react";
import { cva } from "class-variance-authority";
import { cn } from "../../lib/utils";
import { Loader2 } from "lucide-react";

// Helper function to combine classes if not already present
// We need to make sure 'cn' (clsx + tailwind-merge) is available.
// If not, we'll create it in a separate step. For now assume it is there 
// or I will create it next.

const buttonVariants = cva(
    "inline-flex items-center justify-center rounded-lg text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none ring-offset-background",
    {
        variants: {
            variant: {
                default: "bg-blue-600 text-white hover:bg-blue-700",
                destructive:
                    "bg-red-500 text-white hover:bg-red-600",
                outline:
                    "border border-input bg-transparent hover:bg-white/10 text-gray-300 hover:text-white",
                secondary:
                    "bg-white/10 text-white hover:bg-white/20",
                ghost: "hover:bg-white/10 text-gray-400 hover:text-white",
                link: "text-primary underline-offset-4 hover:underline",
                success: "text-green-400 hover:bg-green-400/10",
                danger: "text-red-400 hover:bg-red-400/10",
                warning: "text-yellow-400 hover:bg-yellow-400/10",
                info: "text-blue-400 hover:bg-blue-400/10",
            },
            size: {
                default: "h-10 px-4 py-2",
                sm: "h-9 rounded-md px-3",
                xs: "h-8 px-2 text-xs",
                lg: "h-11 rounded-md px-8",
                icon: "h-9 w-9",
                iconSm: "h-7 w-7 p-1.5",
            },
        },
        defaultVariants: {
            variant: "default",
            size: "default",
        },
    }
);

const Button = React.forwardRef(
    ({ className, variant, size, asChild = false, loading = false, children, disabled, ...props }, ref) => {
        return (
            <button
                className={cn(buttonVariants({ variant, size, className }))}
                ref={ref}
                disabled={disabled || loading}
                {...props}
            >
                {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {children}
            </button>
        );
    }
);
Button.displayName = "Button";

const IconButton = React.forwardRef(
    ({ className, variant = "ghost", size = "iconSm", icon: Icon, ...props }, ref) => {
        return (
            <Button
                variant={variant}
                size={size}
                className={className}
                ref={ref}
                {...props}
            >
                <Icon className="w-4 h-4" />
            </Button>
        )
    }
)
IconButton.displayName = "IconButton";


export { Button, IconButton, buttonVariants };
